import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    PiArrowLeftBold,
    PiArrowSquareOutBold,
    PiBellBold,
    PiCalendarBold,
    PiCheckBold,
    PiCheckCircleBold,
    PiClockBold,
    PiFilePdfBold,
    PiListBold,
    PiUserBold,
    PiWarningCircleBold,
} from 'react-icons/pi';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import FileDropzone from '@v2/components/ui/FileDropzone';
import { ActionIcon, Badge, Button, LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import type {
    CommissionComposition,
    PaymentApprovalAuditLog,
    PaymentApprovalDetailResponse,
    PaymentApprovalMonthlyNf,
    PaymentApprovalNfRecord,
} from './types';

type TabKey = 'nf' | 'preview' | 'vendas' | 'log';

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function fmtPct(v: number | null | undefined) {
    if (v == null || Number.isNaN(v)) return '—';
    return `${v.toFixed(2)}%`;
}

function currentPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fmtPeriod(ym: string) {
    const [y, m] = ym.split('-');
    return `${m}/${y}`;
}

function fmtDateTime(value: string | null | undefined) {
    if (!value) return '—';
    const d = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const STATUS_BADGE_COLOR: Record<string, 'secondary' | 'warning' | 'info' | 'success' | 'danger'> = {
    pending: 'secondary',
    awaiting_nf: 'warning',
    nf_submitted: 'info',
    paid: 'success',
    open: 'secondary',
    cancelled: 'danger',
};

const ACTION_LABEL: Record<string, string> = {
    consolidated: 'NF consolidada',
    nf_requested: 'NF solicitada',
    nf_submitted: 'NF anexada',
    paid: 'Pagamento aprovado',
    cancelled: 'Cancelada',
    payment_batch_approved: 'Lote aprovado',
};

function defaultTab(nf: PaymentApprovalMonthlyNf | null): TabKey {
    if (!nf) return 'nf';
    if (nf.status === 'nf_submitted') return 'preview';
    if (nf.status === 'paid') return 'log';
    return 'nf';
}

function StatusStepper({ nf, hasUnlinked }: { nf: PaymentApprovalMonthlyNf | null; hasUnlinked: boolean }) {
    const status = nf?.status ?? (hasUnlinked ? 'unconsolidated' : 'empty');
    const steps = [
        { key: 'consolidate', label: 'NF do mês' },
        { key: 'attach', label: 'Anexar' },
        { key: 'review', label: 'Conferir' },
        { key: 'paid', label: 'Aprovar' },
    ] as const;

    let currentIdx = 0;
    if (status === 'awaiting_nf' || status === 'open') currentIdx = 1;
    else if (status === 'nf_submitted') currentIdx = 2;
    else if (status === 'paid') currentIdx = 3;

    const allDone = status === 'paid';

    return (
        <ol className="flex w-full items-start px-1 sm:px-4">
            {steps.map((step, idx) => {
                const done = allDone || idx < currentIdx;
                const current = !allDone && idx === currentIdx;
                const isLast = idx === steps.length - 1;
                // Linha até o próximo: preenchida quando a etapa atual já foi concluída
                const lineFilled = done;

                return (
                    <li
                        key={step.key}
                        className="relative flex min-w-0 flex-1 flex-col items-center"
                    >
                        <div className="relative flex h-8 w-full items-center justify-center">
                            {!isLast && (
                                <span
                                    aria-hidden
                                    className={cn(
                                        'absolute left-1/2 top-1/2 z-0 h-0.5 w-full -translate-y-1/2',
                                        lineFilled ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-zinc-700',
                                    )}
                                />
                            )}
                            <span
                                className={cn(
                                    'relative z-10 flex size-8 items-center justify-center rounded-full text-xs font-bold ring-4 ring-white dark:ring-zinc-900',
                                    done && 'bg-emerald-500 text-white',
                                    current && 'bg-sky-600 text-white',
                                    !done && !current && 'bg-gray-200 text-gray-500 dark:bg-zinc-700 dark:text-zinc-400',
                                )}
                            >
                                {done ? <PiCheckBold className="size-4" /> : idx + 1}
                            </span>
                        </div>
                        <span
                            className={cn(
                                'mt-2 whitespace-nowrap text-center text-xs font-semibold',
                                done && 'text-emerald-700 dark:text-emerald-400',
                                current && 'text-sky-700 dark:text-sky-300',
                                !done && !current && 'text-gray-400 dark:text-zinc-500',
                            )}
                        >
                            {step.label}
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}

function CompositionStrip({ composition }: { composition?: CommissionComposition | null }) {
    if (!composition) return null;

    const items = [
        (composition.current_period_count ?? 0) > 0
            ? { label: 'Mês atual', count: composition.current_period_count!, amount: composition.current_period_amount ?? 0 }
            : null,
        (composition.prior_sale_count ?? 0) > 0
            ? { label: 'Venda antiga', count: composition.prior_sale_count!, amount: composition.prior_sale_amount ?? 0 }
            : null,
        composition.deal_count > 0
            ? { label: 'Vendas (deal)', count: composition.deal_count, amount: composition.deal_amount }
            : null,
        composition.installment_count > 0
            ? { label: 'Parcelas (cascata)', count: composition.installment_count, amount: composition.installment_amount }
            : null,
        composition.salary_amount > 0
            ? { label: 'Salário', count: null as number | null, amount: composition.salary_amount }
            : null,
    ].filter(Boolean) as { label: string; count: number | null; amount: number }[];

    if (items.length === 0) return null;

    return (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((item) => (
                <div
                    key={item.label}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-800/50"
                >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{item.label}</p>
                    <p className="mt-0.5 text-sm font-bold text-gray-900 dark:text-white">{fmt(item.amount)}</p>
                    {item.count != null && (
                        <p className="text-xs text-gray-500">{item.count} registro(s)</p>
                    )}
                </div>
            ))}
        </div>
    );
}

function RecordsTable({ records }: { records: PaymentApprovalNfRecord[] }) {
    if (records.length === 0) {
        return <p className="px-4 py-8 text-center text-sm text-gray-400">Nenhuma venda neste período.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
                <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-zinc-800">
                    <tr>
                        <th className="px-3 py-2 text-left">Data deal</th>
                        <th className="px-3 py-2 text-left">Negociação</th>
                        <th className="px-3 py-2 text-left">Origem</th>
                        <th className="px-3 py-2 text-left">Papel</th>
                        <th className="px-3 py-2 text-left">Deal / Cliente</th>
                        <th className="px-3 py-2 text-left">Condição</th>
                        <th className="px-3 py-2 text-right">Base</th>
                        <th className="px-3 py-2 text-right">%</th>
                        <th className="px-3 py-2 text-right">Comissão</th>
                        <th className="px-3 py-2 text-center">Status</th>
                        <th className="px-3 py-2 text-right">Ações</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                    {records.map((r) => (
                        <tr key={r.id} className={r.confidential ? 'bg-red-50/40' : 'hover:bg-gray-50'}>
                            <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                                <p>{r.date ?? '—'}</p>
                                {r.source_type === 'installment' && r.installment_paid_at && (
                                    <p className="text-[10px] text-gray-400">
                                        Paga {fmtDateTime(r.installment_paid_at).split(',')[0]}
                                    </p>
                                )}
                            </td>
                            <td className="px-3 py-2">
                                <span className={cn(
                                    'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold',
                                    r.deal_age === 'prior_sale' && 'bg-amber-100 text-amber-900',
                                    r.deal_age === 'current_period' && 'bg-emerald-100 text-emerald-800',
                                    (!r.deal_age || r.deal_age === 'unknown') && 'bg-gray-100 text-gray-500',
                                )}>
                                    {r.deal_age_label ?? '—'}
                                </span>
                            </td>
                            <td className="px-3 py-2">
                                <span className={cn(
                                    'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold',
                                    r.installment_kind === 'entry' && 'bg-rose-100 text-rose-800',
                                    r.installment_kind === 'installment' && 'bg-violet-100 text-violet-800',
                                    r.source_type === 'deal' && 'bg-sky-100 text-sky-800',
                                )}>
                                    {r.source_label}
                                </span>
                                {r.source_type === 'deal' && r.has_entry && (
                                    <p className="mt-0.5 text-[10px] text-gray-400">
                                        Deal com entrada
                                        {r.entry_pct != null && r.entry_pct > 0
                                            ? ` (${r.entry_pct % 1 === 0 ? r.entry_pct : r.entry_pct.toFixed(1)}%)`
                                            : ''}
                                    </p>
                                )}
                                {r.source_type === 'installment' && r.payment_method_label && (
                                    <p className="mt-0.5 text-[10px] text-gray-400">{r.payment_method_label}</p>
                                )}
                            </td>
                            <td className="px-3 py-2">
                                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-800">
                                    {r.role_label}
                                </span>
                                {r.mode_flags?.filter((f) => !f.label.startsWith('Deflator')).length > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {r.mode_flags
                                            .filter((f) => !f.label.startsWith('Deflator'))
                                            .slice(0, 3)
                                            .map((f) => (
                                                <span key={f.key} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                                                    {f.label}
                                                </span>
                                            ))}
                                    </div>
                                )}
                            </td>
                            <td className="px-3 py-2">
                                <p className="font-semibold text-gray-900">{r.deal_number ?? `#${r.ploomes_deal_id}`}</p>
                                <p className="max-w-[200px] truncate text-xs text-gray-500">{r.client_name ?? '—'}</p>
                                {r.deal_title && <p className="max-w-[200px] truncate text-[11px] text-gray-400">{r.deal_title}</p>}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600">
                                {r.payment_condition ?? '—'}
                                {r.payment_parcelas != null && r.payment_parcelas > 0 && (
                                    <span className="text-gray-400"> · {r.payment_parcelas}x</span>
                                )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right">{fmt(r.base_amount)}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-right text-gray-600">{fmtPct(r.rate_pp)}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-right font-bold text-emerald-700">{fmt(r.final_amount)}</td>
                            <td className="px-3 py-2 text-center">
                                <Badge
                                    color={STATUS_BADGE_COLOR[r.status] ?? 'secondary'}
                                    variant="flat"
                                    rounded="pill"
                                    size="sm"
                                >
                                    {r.status_label}
                                </Badge>
                            </td>
                            <td className="px-3 py-2 text-right">
                                {r.deal_url ? (
                                    <Link to={r.deal_url}>
                                        <Button size="sm" variant="outline" className="gap-1">
                                            Ver
                                            <PiArrowSquareOutBold className="size-3.5" />
                                        </Button>
                                    </Link>
                                ) : '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AuditTimeline({ logs }: { logs: PaymentApprovalAuditLog[] }) {
    if (logs.length === 0) {
        return <p className="px-4 py-8 text-center text-sm text-gray-400">Nenhum evento ainda.</p>;
    }

    return (
        <ul className="max-h-[420px] space-y-3 overflow-y-auto px-4 py-4">
            {logs.map((log) => (
                <li key={log.id} className="border-l-2 border-gray-200 pl-3 dark:border-zinc-700">
                    <p className="text-sm font-semibold text-gray-900">
                        {ACTION_LABEL[log.action] ?? log.action}
                    </p>
                    {log.description && <p className="text-xs text-gray-600">{log.description}</p>}
                    <p className="mt-0.5 text-[11px] text-gray-400">
                        {log.changed_by ?? 'Sistema'} · {fmtDateTime(log.created_at)}
                    </p>
                </li>
            ))}
        </ul>
    );
}

export default function CommissionPaymentApprovalDetailPage() {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const period = searchParams.get('period') ?? currentPeriod();
    const qc = useQueryClient();

    const [notes, setNotes] = useState('');
    const [approveOpen, setApproveOpen] = useState(false);
    const [tabOverride, setTabOverride] = useState<TabKey | null>(null);

    const { data, isLoading, isError } = useQuery<{ data: PaymentApprovalDetailResponse }>({
        queryKey: ['commissioning', 'payment-approvals', userId, period],
        queryFn: () => api.get(`/commissioning/payment-approvals/${userId}`, {
            params: period ? { period } : {},
        }).then((r) => r.data),
        enabled: !!userId,
    });

    const detail = data?.data;
    const nfs = detail?.monthly_nfs ?? [];
    const nf = nfs.find((n) => n.period === period) ?? nfs[0] ?? null;
    const records = detail?.records ?? [];
    const backUrl = `/commissioning/payment-approvals?period=${period}`;
    const unlinkedCount = detail?.unlinked_records_count ?? 0;

    const tab = tabOverride ?? defaultTab(nf);

    const { data: periodsData } = useQuery<{ meta: { available_periods: string[] } }>({
        queryKey: ['commissioning', 'payment-approvals-periods'],
        queryFn: () => api.get('/commissioning/payment-approvals').then((r) => r.data),
        staleTime: 60_000,
    });

    const approveMut = useMutation({
        mutationFn: () => api.post(`/commissioning/payment-approvals/${userId}/approve`, {
            monthly_nf_ids: nf ? [nf.id] : [],
            notes: notes || undefined,
        }),
        onSuccess: (res) => {
            toast.success(res.data.message ?? 'Pagamento aprovado!');
            qc.invalidateQueries({ queryKey: ['commissioning'] });
            navigate(`/commissioning/payment-approvals?period=${period}`);
        },
        onError: (err: { response?: { data?: { message?: string } } }) =>
            toast.error(err?.response?.data?.message ?? 'Erro ao aprovar.'),
    });

    const requestNfMut = useMutation({
        mutationFn: () => api.post(`/commissioning/payment-approvals/${userId}/request-nf`, {
            monthly_nf_ids: nf ? [nf.id] : [],
        }),
        onSuccess: (res) => {
            toast.success(res.data.message ?? 'NF solicitada!');
            qc.invalidateQueries({ queryKey: ['commissioning'] });
        },
        onError: (err: { response?: { data?: { message?: string } } }) =>
            toast.error(err?.response?.data?.message ?? 'Erro ao solicitar NF.'),
    });

    const uploadMut = useMutation({
        mutationFn: async (file: File) => {
            if (!nf) throw new Error('Sem NF mensal');
            const form = new FormData();
            form.append('invoice', file);
            return api.post(`/commissioning/monthly-nfs/${nf.id}/submit-nf`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => {
            toast.success('NF anexada com sucesso!');
            setTabOverride('preview');
            qc.invalidateQueries({ queryKey: ['commissioning'] });
        },
        onError: (err: { response?: { data?: { message?: string } } }) =>
            toast.error(err?.response?.data?.message ?? 'Erro ao anexar NF (PDF/XML, máx. 10MB).'),
    });

    const canAttach = !!nf && ['open', 'awaiting_nf'].includes(nf.status);
    const canApprove = !!nf && nf.status === 'nf_submitted' && nf.totals_match;
    const isPdf = !!nf?.invoice_url && !nf.invoice_url.toLowerCase().includes('.xml');
    const logCount = nf?.audit_logs?.length ?? 0;

    const tabs = useMemo(() => [
        { key: 'nf' as const, label: 'NF do mês', icon: PiCheckCircleBold },
        {
            key: 'preview' as const,
            label: 'Preview',
            icon: PiFilePdfBold,
            disabled: !nf?.invoice_url,
        },
        { key: 'vendas' as const, label: `Vendas (${records.length})`, icon: PiListBold },
        { key: 'log' as const, label: `Log (${logCount})`, icon: PiClockBold },
    ], [nf?.invoice_url, records.length, logCount]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <Link to={backUrl}>
                    <ActionIcon variant="outline" rounded="full" size="sm" aria-label="Voltar">
                        <PiArrowLeftBold className="size-4" />
                    </ActionIcon>
                </Link>
                <PageHeader
                    className="min-w-0 flex-1 !mb-0"
                    title={detail ? `NF — ${detail.seller.name}` : 'NF mensal'}
                    subtitle={`1 NF por mês · ${fmtPeriod(period)}`}
                />
                <div className="flex items-center gap-2">
                    <PiCalendarBold className="size-4 text-gray-400" />
                    <select
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm focus:border-primary focus:outline-none"
                        value={period}
                        onChange={(e) => {
                            setTabOverride(null);
                            setSearchParams({ period: e.target.value });
                        }}
                    >
                        {(periodsData?.meta?.available_periods ?? [period]).map((p) => (
                            <option key={p} value={p}>{fmtPeriod(p)}</option>
                        ))}
                    </select>
                </div>
            </div>

            {isLoading && <LoadingState variant="detail" />}
            {isError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    Não foi possível carregar este vendedor.
                </div>
            )}

            {detail && (
                <>
                    {/* Header compacto */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex min-w-0 items-center gap-3">
                                {detail.seller.avatar_url ? (
                                    <img src={detail.seller.avatar_url} alt="" className="size-11 rounded-xl object-cover ring-2 ring-gray-100" />
                                ) : (
                                    <span className="flex size-11 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
                                        <PiUserBold className="size-5" />
                                    </span>
                                )}
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <p className="truncate font-bold text-gray-900 dark:text-zinc-100">{detail.seller.name}</p>
                                        {nf && (
                                            <Badge
                                                color={STATUS_BADGE_COLOR[nf.status] ?? 'secondary'}
                                                variant="flat"
                                                rounded="pill"
                                                size="sm"
                                            >
                                                {nf.status_label}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="truncate text-xs text-gray-500">{detail.seller.email}</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-4 text-sm">
                                <div>
                                    <p className="text-[10px] uppercase text-gray-400">Base</p>
                                    <p className="font-semibold">{fmt(detail.summary.total_base ?? 0)}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase text-gray-400">Comissão</p>
                                    <p className="font-semibold text-emerald-700">{fmt(detail.summary.total_commissions)}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase text-gray-400">Total NF</p>
                                    <p className="font-black text-emerald-700">{fmt(detail.summary.total_amount)}</p>
                                </div>
                                {detail.summary.total_salary > 0 && (
                                    <div>
                                        <p className="text-[10px] uppercase text-gray-400">Salário</p>
                                        <p className="font-semibold">{fmt(detail.summary.total_salary)}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-3 border-t border-gray-100 pt-3 dark:border-zinc-800">
                            <StatusStepper nf={nf} hasUnlinked={unlinkedCount > 0} />
                        </div>

                        <div className="mt-3">
                            <CompositionStrip composition={nf?.composition ?? detail.composition ?? detail.summary.composition} />
                        </div>

                        {nf && !nf.totals_match && (
                            <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                                <PiWarningCircleBold className="mt-0.5 size-3.5 shrink-0" />
                                Totais divergem da soma das vendas
                                {nf.mismatch_amount ? ` (${fmt(Math.abs(nf.mismatch_amount))})` : ''}.
                            </div>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="flex gap-1 overflow-x-auto border-b border-gray-100 bg-gray-50 p-1.5 dark:border-zinc-800 dark:bg-zinc-800/60">
                            {tabs.map((t) => {
                                const Icon = t.icon;
                                const active = tab === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        type="button"
                                        disabled={t.disabled}
                                        onClick={() => setTabOverride(t.key)}
                                        className={cn(
                                            'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition',
                                            active
                                                ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100'
                                                : 'text-gray-500 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40',
                                        )}
                                    >
                                        <Icon className="size-4" />
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Tab: NF */}
                        {tab === 'nf' && (
                            <div className="space-y-4 p-4">
                                {!nf && (
                                    <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
                                        <p className="text-sm text-orange-900">
                                            Sem NF mensal neste período.
                                            {unlinkedCount > 0 && <> Há <strong>{unlinkedCount}</strong> venda(s) para consolidar.</>}
                                        </p>
                                        <Button
                                            color="warning"
                                            className="mt-3 gap-2"
                                            isLoading={requestNfMut.isPending}
                                            onClick={() => requestNfMut.mutate()}
                                        >
                                            <PiBellBold className="size-4" />
                                            Criar NF do mês e solicitar
                                        </Button>
                                    </div>
                                )}

                                {canAttach && nf && (
                                    <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                                        <div>
                                            <p className="text-sm font-semibold text-amber-900">Anexar NF (PDF/XML)</p>
                                            <p className="mt-1 text-xs text-amber-800">
                                                Valor: <strong>{fmt(nf.total_amount)}</strong> · vendedor ou RH podem anexar
                                            </p>
                                        </div>
                                        <FileDropzone
                                            accept=".pdf,.xml,application/pdf,text/xml"
                                            acceptLabel="PDF ou XML"
                                            maxSizeMb={10}
                                            loading={uploadMut.isPending}
                                            disabled={uploadMut.isPending}
                                            label="Arraste a NF aqui"
                                            hint="ou clique para selecionar o arquivo"
                                            onFiles={(files) => {
                                                if (files[0]) uploadMut.mutate(files[0]);
                                            }}
                                            onError={(msg) => toast.error(msg)}
                                        />
                                        <Button
                                            variant="outline"
                                            color="warning"
                                            className="gap-2"
                                            isLoading={requestNfMut.isPending}
                                            onClick={() => requestNfMut.mutate()}
                                        >
                                            <PiBellBold className="size-4" />
                                            Cobrar vendedor
                                        </Button>
                                    </div>
                                )}

                                {nf?.status === 'nf_submitted' && (
                                    <div className="space-y-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4">
                                        <p className="text-sm font-semibold text-blue-900">
                                            NF anexada — conferir e aprovar
                                        </p>
                                        <p className="text-xs text-blue-800">
                                            Enviada em {fmtDateTime(nf.invoice_submitted_at)}. Use a aba Preview para visualizar.
                                        </p>
                                        <textarea
                                            className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
                                            rows={2}
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="Observação da aprovação (opcional)"
                                        />
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                variant="outline"
                                                className="gap-2"
                                                onClick={() => setTabOverride('preview')}
                                            >
                                                <PiFilePdfBold className="size-4" />
                                                Ver preview
                                            </Button>
                                            <Button
                                                color="success"
                                                className="gap-2"
                                                disabled={!canApprove}
                                                isLoading={approveMut.isPending}
                                                onClick={() => setApproveOpen(true)}
                                            >
                                                <PiCheckBold className="size-4" />
                                                Aprovar — {fmt(nf.total_amount)}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {nf?.status === 'paid' && (
                                    <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                                        <PiCheckCircleBold className="mt-0.5 size-5 text-emerald-600" />
                                        <div>
                                            <p className="text-sm font-semibold text-emerald-900">Pagamento aprovado</p>
                                            <p className="text-xs text-emerald-700">
                                                {fmtDateTime(nf.paid_at)}
                                                {nf.paid_by_name ? ` · ${nf.paid_by_name}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {nf && nf.status !== 'nf_submitted' && nf.status !== 'paid' && !canAttach && (
                                    <p className="text-sm text-gray-500">Status atual: {nf.status_label}</p>
                                )}
                            </div>
                        )}

                        {/* Tab: Preview */}
                        {tab === 'preview' && nf?.invoice_url && (
                            <div>
                                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
                                    <p className="text-sm text-gray-600">
                                        Enviada em {fmtDateTime(nf.invoice_submitted_at)}
                                    </p>
                                    <a href={nf.invoice_url} target="_blank" rel="noopener noreferrer">
                                        <Button size="sm" variant="outline" className="gap-1.5">
                                            <PiFilePdfBold className="size-3.5" />
                                            Abrir em nova aba
                                        </Button>
                                    </a>
                                </div>
                                {isPdf ? (
                                    <iframe
                                        title={`Preview NF ${nf.period_label}`}
                                        src={nf.invoice_url}
                                        className="h-[min(70vh,560px)] w-full bg-white"
                                    />
                                ) : (
                                    <p className="px-4 py-8 text-center text-sm text-gray-500">
                                        Arquivo XML — abra em nova aba para visualizar.
                                    </p>
                                )}
                                {nf.status === 'nf_submitted' && (
                                    <div className="flex justify-end border-t border-gray-100 px-4 py-3 dark:border-zinc-800">
                                        <Button
                                            color="success"
                                            className="gap-2"
                                            disabled={!canApprove}
                                            isLoading={approveMut.isPending}
                                            onClick={() => setApproveOpen(true)}
                                        >
                                            <PiCheckBold className="size-4" />
                                            Aprovar — {fmt(nf.total_amount)}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {tab === 'vendas' && (
                            <div className="space-y-3 p-4">
                                <CompositionStrip composition={nf?.composition ?? detail.composition} />
                                <p className="text-xs text-gray-500">
                                    Mês atual / Venda antiga = data do deal vs período da NF ·
                                    Venda = comissão no ganho · Entrada / Parcela N/M = cascata na cobrança paga
                                    (entrada = ordinal 0).
                                </p>
                                <RecordsTable records={records} />
                            </div>
                        )}

                        {tab === 'log' && <AuditTimeline logs={nf?.audit_logs ?? []} />}
                    </div>

                    <ConfirmModal
                        open={approveOpen}
                        onClose={() => setApproveOpen(false)}
                        onConfirm={() => {
                            setApproveOpen(false);
                            approveMut.mutate();
                        }}
                        title="Aprovar pagamento da NF mensal"
                        description={
                            nf
                                ? `Aprovar NF de ${nf.period_label} (${detail.seller.name})? Total: ${fmt(nf.total_amount)}. Status → Pago e fica no log quem validou.`
                                : ''
                        }
                        confirmLabel="Sim, aprovar"
                        confirmClass="bg-emerald-600 hover:bg-emerald-700"
                    />
                </>
            )}
        </div>
    );
}
